import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import PuzzleGame, { PUZZLE_LEVEL_COUNT } from './PuzzleGame';
import ColoringGame from './ColoringGame';
import PuzzleBlockCoding, { CODING_MISSION_COUNT } from './PuzzleBlockCoding';
import OddOneOut from './OddOneOut';
import FixThePattern from './FixThePattern';
import { MemoryMatch, BigOrSmall, WhatsMissing, PictureQuiz } from './BrainBoostExtraGames';
import { PRESCHOOL_QUIZZES } from './preschoolQuizzes';
import { VolumeBar, useVolume, setVolume, runReplay, useHud } from './lzAudio';
import { GameHeaderBar } from './GameHeaderBar';
import {
    getEffectiveStudentId,
    getSelectedChildProfile,
    isParentLoginProfile,
} from '../../utilities/teacherModulePermissions';

// Score / question / volume bar shared with every other game, fed by
// whatever the running Brain Boost game reports through setHud().
const BrainHeader = () => {
    const volume = useVolume();
    const hud = useHud() || {};
    return (
        <GameHeaderBar
            scoreIcon={hud.scoreIcon}
            scoreLabel={hud.scoreLabel}
            score={hud.score ?? 0}
            total={hud.total}
            // A full-sentence question ("Is the sun hot or cold?") reads
            // like the "Tap the letter:" label; a label + short answer
            // ("Make: 🐶 Dog") uses the big blue value, as in Tap the Letter.
            questionLabel={hud.questionLabel || hud.question}
            question={hud.questionLabel ? hud.question : null}
            questionSize="lg"
            volume={volume}
            onVolumeChange={setVolume}
            onReplay={runReplay}
        />
    );
};

// Built-in Learning Zone games, rendered in the same level-card + "Level N"
// modal format as the API-driven games in GamelevelItems.js. They don't come
// from GAME_LIST (no game_name_id to GAME_SAVE against), so completion and
// stars are kept per child in localStorage, and all levels are always open.

const GAMES = [
    { id: 'puzzle', title: 'Jigsaw Puzzles', icon: '🧩', age: 'Ages 3–5', skill: 'Spatial reasoning' },
    { id: 'coloring', title: 'Color & Mix', icon: '🎨', age: 'Ages 3–5', skill: 'Creativity' },
    { id: 'coding', title: 'Block Coding', icon: '🤖', age: 'Ages 3–5', skill: 'Logic & sequencing' },
    { id: 'oddOneOut', title: 'Odd One Out', icon: '🔍', age: 'Ages 2–5', skill: 'Critical thinking' },
    { id: 'pattern', title: 'Fix the Pattern', icon: '🔁', age: 'Ages 2–5', skill: 'Pattern reasoning' },
];

const PROGRESS_KEY = 'syntch_lz_progress';
const FONT = 'Baloo 2, Fredoka One, Comic Sans MS, cursive';

// Age in half-years (3, 3.5, 4 …) from a dob string, or undefined.
const ageFromDob = (dob) => {
    if (!dob) return undefined;
    const born = new Date(dob);
    if (Number.isNaN(born.getTime())) return undefined;
    const years = (Date.now() - born.getTime()) / (365.25 * 24 * 3600 * 1000);
    return years > 0 ? Math.floor(years * 2) / 2 : undefined;
};

const starsFor = (pct) => (pct >= 100 ? 3 : pct >= 70 ? 2 : pct >= 40 ? 1 : 0);

const reportResult = (game, payload) => {
    // No backend endpoint exists for these games yet; this is the single
    // hook point to wire them into the GameEngine service when it does.
    console.info(`[LearningZone] ${game}`, payload);
};

const readProgress = (key) => {
    try {
        const raw = window.localStorage.getItem(key);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
        return {};
    }
};

// ---------------------------------------------------------------------------
// API-driven Brain Boost games. The Brain Boost category and its games now
// come from GAME_CATEGORY_LIST / GAME_LIST like every other category, and
// GamelevelItems renders them through the same level / lock / GAME_SAVE /
// "Level Completed!" flow. Their backend ids aren't fixed in the frontend
// (unlike ids 1-22), so a game is recognised by its name instead.
// ---------------------------------------------------------------------------
const BRAIN_GAME_MATCHERS = [
    { key: 'puzzle', test: /jigsaw|puzzle/i },
    { key: 'coloring', test: /colou?r\s*(&|and)?\s*mix|colou?ring/i },
    { key: 'coding', test: /coding|code/i },
    { key: 'oddOneOut', test: /odd\s*one/i },
    { key: 'pattern', test: /pattern/i },
    { key: 'memory', test: /memory|flip/i },
    { key: 'bigSmall', test: /big|small|size/i },
    { key: 'missing', test: /missing/i },
    { key: 'animalHomes', test: /animal\s*home|where.*live/i },
    { key: 'goesTogether', test: /together|goes\s*with/i },
    { key: 'feelings', test: /feeling|emotion/i },
    { key: 'hotCold', test: /\bhot\b|\bcold\b/i },
];

export const getBrainGameKey = (name) => {
    const text = String(name || '');
    const hit = BRAIN_GAME_MATCHERS.find((m) => m.test.test(text));
    return hit ? hit.key : null;
};

// Brain Boost games that ship in the frontend before the backend lists them.
// GamelevelItems appends them to the Brain Boost category with local string
// ids and keeps their completion/stars per child in localStorage. Once
// GAME_LIST returns a game whose name maps to the same key (see
// BRAIN_GAME_MATCHERS), the API game is used and the static copy is dropped -
// so going dynamic needs no frontend change.
const STATIC_BRAIN_GAMES = [
    { key: 'memory', name: 'Memory Match', icon: '🃏' },
    { key: 'bigSmall', name: 'Big & Small', icon: '🐘' },
    { key: 'missing', name: "What's Missing?", icon: '👀' },
    { key: 'animalHomes', name: 'Animal Homes', icon: '🏡' },
    { key: 'goesTogether', name: 'Goes Together', icon: '🧦' },
    { key: 'feelings', name: 'Feelings', icon: '😊' },
    { key: 'hotCold', name: 'Hot or Cold', icon: '🌡️' },
];
const STATIC_BRAIN_PROGRESS_KEY = 'brainBoostStaticProgress';

export const isBrainBoostCategory = (name) => /brain/i.test(String(name || ''));

const staticProgressKey = (childId) => (childId ? `${STATIC_BRAIN_PROGRESS_KEY}_${childId}` : STATIC_BRAIN_PROGRESS_KEY);

export const buildStaticBrainGames = (apiGames = [], childId) => {
    const apiKeys = new Set(apiGames.map((g) => getBrainGameKey(g?.name)).filter(Boolean));
    const progress = readProgress(staticProgressKey(childId)); // { key: stars }
    return STATIC_BRAIN_GAMES
        .filter((g) => !apiKeys.has(g.key))
        .map((g) => ({
            id: `static_brain_${g.key}`,
            name: g.name,
            icon: g.icon,
            staticKey: g.key,
            is_completed: g.key in progress,
            stars: progress[g.key] || 0,
            score: Math.round(((progress[g.key] || 0) / 3) * 100),
        }));
};

export const saveStaticBrainProgress = (childId, key, stars) => {
    const storageKey = staticProgressKey(childId);
    const progress = readProgress(storageKey);
    progress[key] = Math.max(progress[key] || 0, stars);
    try { window.localStorage.setItem(storageKey, JSON.stringify(progress)); } catch (e) { /* ignore */ }
};

// Plays one Brain Boost game and reports completion as (stars, score),
// matching the onGameComplete contract of the other game components.
export const BrainGameBoard = ({ gameKey, onGameComplete }) => {
    let profileData = useSelector((state) => state.setting.setting.user_data);
    profileData = profileData ? JSON.parse(profileData) : '';

    const child = isParentLoginProfile(profileData) ? getSelectedChildProfile(profileData) : profileData;
    const childId = String(getEffectiveStudentId(profileData) ?? '') || undefined;
    const childName = child?.first_name || undefined;
    const childAge = ageFromDob(child?.user_setting?.dob || child?.dob);

    const session = useRef({ done: false, correct: 0 });
    const timer = useRef(null);
    useEffect(() => {
        session.current = { done: false, correct: 0 };
        return () => clearTimeout(timer.current);
    }, [gameKey]);

    const complete = (stars, delay = 0) => {
        if (session.current.done) return;
        session.current.done = true;
        const safeStars = Math.max(1, Math.min(3, stars || 1));
        timer.current = setTimeout(() => {
            onGameComplete?.(safeStars, Math.round((safeStars / 3) * 100));
        }, delay);
    };

    let content = null;
    // Preschool picture quizzes share one component, driven by data.
    const quiz = PRESCHOOL_QUIZZES[gameKey];
    switch (quiz ? 'quiz' : gameKey) {
        case 'quiz':
            content = <PictureQuiz key={gameKey} quiz={quiz} onFinish={(correct, total) => complete(starsFor(Math.round((correct / total) * 100)), 1500)} />;
            break;
        case 'puzzle':
            content = (
                <PuzzleGame
                    childAge={childAge}
                    childName={childName}
                    onComplete={(data) => {
                        reportResult('puzzle', data);
                        if (data.levelId === PUZZLE_LEVEL_COUNT) complete(data.wrongMoves === 0 ? 3 : data.wrongMoves <= 3 ? 2 : 1, 2500);
                    }}
                />
            );
            break;
        case 'coloring':
            content = (
                <ColoringGame
                    childId={childId}
                    childAge={childAge}
                    onColorFound={(id, label, total) => reportResult('coloring:color', { id, label, total })}
                    onImageComplete={(image, colorMap) => {
                        reportResult('coloring:image', { image, colorMap });
                        complete(3, 1500);
                    }}
                />
            );
            break;
        case 'coding':
            content = (
                <PuzzleBlockCoding
                    childId={childId}
                    childAge={childAge}
                    onComplete={(data) => reportResult('coding', data)}
                    onMissionComplete={(missionId, stars) => {
                        reportResult('coding:mission', { missionId, stars });
                        if (missionId === CODING_MISSION_COUNT) complete(stars, 3000);
                    }}
                />
            );
            break;
        case 'oddOneOut':
            content = (
                <OddOneOut
                    childId={childId}
                    childAge={childAge}
                    onComplete={(levelId, correct, timeMs) => reportResult('oddOneOut', { levelId, correct, timeMs })}
                    onFinish={(correct, total) => complete(starsFor(Math.round((correct / total) * 100)), 1500)}
                />
            );
            break;
        case 'pattern':
            content = (
                <FixThePattern
                    childId={childId}
                    childAge={childAge}
                    onComplete={(levelId, correct, patternType) => {
                        reportResult('pattern', { levelId, correct, patternType });
                        if (correct) session.current.correct += 1;
                        if (levelId === 10) complete(starsFor(session.current.correct * 10), 2400);
                    }}
                />
            );
            break;
        case 'memory':
            content = <MemoryMatch onFinish={(correct, total) => complete(starsFor(Math.round((correct / total) * 100)), 1500)} />;
            break;
        case 'bigSmall':
            content = <BigOrSmall onFinish={(correct, total) => complete(starsFor(Math.round((correct / total) * 100)), 1500)} />;
            break;
        case 'missing':
            content = <WhatsMissing onFinish={(correct, total) => complete(starsFor(Math.round((correct / total) * 100)), 1500)} />;
            break;
        default:
            return null;
    }

    // Coloring and Block Coding lay out three columns, so their board is
    // allowed to grow wider than the standard 900px game board.
    const wide = gameKey === 'coloring' || gameKey === 'coding';

    return (
        <div className={`bgb-wrap ${wide ? 'bgb-wide' : ''}`}>
            {/* Same board as every other game (dark wooden board with the
                score / question / volume bar on top); the volume drives
                every sound and spoken line. */}
            <BrainHeader />
            <div className="bgb-board">{content}</div>
            <style>{`
                .bgb-wrap {
                    background: url('/image/board.png'), linear-gradient(135deg, rgba(62, 62, 62, 0.3) 0%, rgba(15, 15, 14, 0.41) 100%);
                    background-size: cover, cover;
                    background-position: center, center;
                    border-radius: 32px;
                    box-shadow: 0 6px 32px #e0c68a33;
                    padding: 20px 12px 16px;
                    margin: 0 auto;
                    width: 98%;
                    max-width: 900px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    box-sizing: border-box;
                }
                .bgb-wrap.bgb-wide { max-width: 1080px; }
                .bgb-board {
                    width: 100%;
                    background: linear-gradient(135deg, rgba(250, 248, 240, 0.95) 0%, rgba(248, 236, 200, 0.95) 100%);
                    border: 2px solid rgba(223, 214, 192, 0.65);
                    border-radius: 24px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10);
                }
                @media (max-width: 700px) {
                    .bgb-wrap { padding: 10px 2vw; }
                }
            `}</style>
        </div>
    );
};

const LearningZoneGames = () => {
    let profileData = useSelector((state) => state.setting.setting.user_data);
    profileData = profileData ? JSON.parse(profileData) : '';

    const child = isParentLoginProfile(profileData) ? getSelectedChildProfile(profileData) : profileData;
    const childId = String(getEffectiveStudentId(profileData) ?? '') || undefined;
    const childName = child?.first_name || undefined;
    const childAge = ageFromDob(child?.user_setting?.dob || child?.dob);
    const storageKey = childId ? `${PROGRESS_KEY}_${childId}` : PROGRESS_KEY;

    const [progress, setProgress] = useState(() => readProgress(storageKey)); // { gameId: stars }
    const [activeIndex, setActiveIndex] = useState(null);
    const session = useRef({ done: false, correct: 0 });
    const timer = useRef(null);

    useEffect(() => { setProgress(readProgress(storageKey)); }, [storageKey]);
    useEffect(() => () => clearTimeout(timer.current), []);

    const game = activeIndex !== null ? GAMES[activeIndex] : null;

    const openGame = (index) => {
        clearTimeout(timer.current);
        session.current = { done: false, correct: 0 };
        setActiveIndex(index);
    };
    const closeGame = () => {
        clearTimeout(timer.current);
        setActiveIndex(null);
    };

    // Same completion flow as GamelevelItems' handleGameComplete: record the
    // result, close the game modal, then the "Level Completed!" dialog with a
    // real "Continue to Next Level" when there is one.
    // `stay`: record the stars but keep the game open (Color & Mix has its own
    // celebration and more pictures to color, so closing would cut play short).
    const finishGame = (stars, delay, stay = false) => {
        if (session.current.done) return;
        session.current.done = true;
        const index = activeIndex;
        const g = GAMES[index];
        timer.current = setTimeout(async () => {
            setProgress((prev) => {
                const next = { ...prev, [g.id]: Math.max(prev[g.id] || 0, stars) };
                try { window.localStorage.setItem(storageKey, JSON.stringify(next)); } catch (e) { /* ignore */ }
                return next;
            });
            if (stay) return;
            setActiveIndex(null);
            const nextIndex = index + 1 < GAMES.length ? index + 1 : null;
            const result = await Swal.fire({
                title: '🎉 Level Completed!',
                html: `
                    <div style="text-align: center;">
                        <h4>Level ${index + 1} - ${g.title}</h4>
                        <div style="font-size: 2rem; margin: 1rem 0;">${'⭐'.repeat(stars) || '💪'}</div>
                        <p style="font-size: 1.1rem; color: #4CAF50; font-weight: 600;">
                            ${nextIndex !== null ? 'Next level unlocked!' : 'You completed all Brain Games!'}
                        </p>
                    </div>
                `,
                icon: 'success',
                confirmButtonText: nextIndex !== null ? 'Continue to Next Level' : 'Great!',
                confirmButtonColor: '#4CAF50',
                background: '#f0f8ff',
            });
            if (nextIndex !== null && result?.isConfirmed) openGame(nextIndex);
        }, delay);
    };

    const renderGame = () => {
        switch (game?.id) {
            case 'puzzle':
                return (
                    <PuzzleGame
                        childAge={childAge}
                        childName={childName}
                        onComplete={(data) => {
                            reportResult('puzzle', data);
                            if (data.levelId === PUZZLE_LEVEL_COUNT) finishGame(data.wrongMoves === 0 ? 3 : data.wrongMoves <= 3 ? 2 : 1, 2500);
                        }}
                    />
                );
            case 'coloring':
                return (
                    <ColoringGame
                        childId={childId}
                        childAge={childAge}
                        onColorFound={(id, label, total) => reportResult('coloring:color', { id, label, total })}
                        onImageComplete={(image, colorMap) => {
                            reportResult('coloring:image', { image, colorMap });
                            finishGame(3, 0, true);
                        }}
                    />
                );
            case 'coding':
                return (
                    <PuzzleBlockCoding
                        childId={childId}
                        childAge={childAge}
                        onComplete={(data) => reportResult('coding', data)}
                        onMissionComplete={(missionId, stars) => {
                            reportResult('coding:mission', { missionId, stars });
                            if (missionId === CODING_MISSION_COUNT) finishGame(stars, 3000);
                        }}
                    />
                );
            case 'oddOneOut':
                return (
                    <OddOneOut
                        childId={childId}
                        childAge={childAge}
                        onComplete={(levelId, correct, timeMs) => reportResult('oddOneOut', { levelId, correct, timeMs })}
                        onFinish={(correct, total) => finishGame(starsFor(Math.round((correct / total) * 100)), 0, true)}
                    />
                );
            case 'pattern':
                return (
                    <FixThePattern
                        childId={childId}
                        childAge={childAge}
                        onComplete={(levelId, correct, patternType) => {
                            reportResult('pattern', { levelId, correct, patternType });
                            if (correct) session.current.correct += 1;
                            if (levelId === 10) finishGame(starsFor(session.current.correct * 10), 2400);
                        }}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <div className="lz-grid">
                {GAMES.map((g, index) => {
                    const stars = progress[g.id] || 0;
                    const isCompleted = g.id in progress;
                    return (
                        <div
                            key={g.id}
                            role="button"
                            tabIndex={0}
                            className={`lz-card ${isCompleted ? 'completed' : ''}`}
                            onClick={() => openGame(index)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openGame(index); }}
                        >
                            <div className={`lz-level-badge ${isCompleted ? 'completed' : ''}`}>
                                {isCompleted ? '🏆' : index + 1}
                            </div>
                            <div className="lz-icon">{g.icon}</div>
                            <h6 className="lz-title">{g.title}</h6>
                            <div className="lz-meta">{g.age} · {g.skill}</div>
                            <div className="lz-stars">
                                {[1, 2, 3].map((s) => (
                                    <span key={s} className={stars >= s ? 'on' : ''} style={{ animationDelay: `${s * 0.2}s` }}>⭐</span>
                                ))}
                            </div>
                            <div className={`lz-status ${isCompleted ? 'completed' : ''}`}>
                                {isCompleted ? (<><span style={{ fontSize: '1.2rem' }}>🏅</span> Completed!</>) : '▶️ Play Now!'}
                            </div>
                        </div>
                    );
                })}
            </div>

            <Modal scrollable show={activeIndex !== null} onHide={closeGame} size="xl" className="lz-modal" style={{ marginTop: 50 }}>
                <Modal.Header className="d-flex justify-content-between align-items-center flex-wrap" style={{ border: 'none', background: 'none', padding: '24px 36px 0 36px', gap: 10 }}>
                    <h4 className="modal-title lz-modal-title">
                        Level {activeIndex !== null ? activeIndex + 1 : ''}{game ? ` · ${game.title}` : ''}
                    </h4>
                    {/* Same volume / replay control as the other games' boards;
                        drives every sound and spoken line in all five games. */}
                    <VolumeBar className="ms-auto" />
                    <button type="button" className="btn btn-secondary lh-1 lz-close" onClick={closeGame}>
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </Modal.Header>
                <Modal.Body style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 170px)', width: '100%', padding: '12px 16px 20px', display: 'flex', justifyContent: 'center' }}>
                    <div className="lz-board">{renderGame()}</div>
                </Modal.Body>
            </Modal>

            <style>{`
                .lz-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
                    row-gap: 4rem;
                    column-gap: 2.5rem;
                    padding: 2.5rem 1rem;
                    width: 100%;
                    position: relative;
                    z-index: 1;
                    background: linear-gradient(135deg, #f8fffc 60%, #e0f7fa 100%);
                    border-radius: 40px;
                    box-shadow: 0 12px 48px 0 rgba(0, 200, 255, 0.10);
                    box-sizing: border-box;
                }
                .lz-card {
                    position: relative;
                    background: linear-gradient(135deg, #FFD700 60%, #FFA500 100%);
                    border-radius: 44px;
                    padding: 2.1rem 1.2rem 2.2rem;
                    text-align: center;
                    cursor: pointer;
                    min-height: 330px;
                    box-sizing: border-box;
                    box-shadow: 0 14px 40px rgba(0, 0, 0, 0.13);
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    font-family: ${FONT};
                    outline: none;
                }
                .lz-card.completed { box-shadow: 0 24px 80px rgba(255, 152, 0, 0.22); filter: drop-shadow(0 0 18px #FFD70088); }
                .lz-card:hover, .lz-card:focus-visible { transform: translateY(-16px) scale(1.06); box-shadow: 0 24px 80px rgba(76, 175, 80, 0.22); }
                .lz-level-badge {
                    position: absolute; top: -18px; right: -18px; width: 60px; height: 60px; border-radius: 50%;
                    background: radial-gradient(circle at 60% 40%, #4CAF50, #388E3C 80%);
                    display: flex; align-items: center; justify-content: center;
                    color: #fff; font-weight: bold; font-size: 1.5rem;
                    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18); border: 4px solid #fff; z-index: 3;
                    font-family: ${FONT};
                }
                .lz-level-badge.completed { background: radial-gradient(circle at 60% 40%, #FFD700, #FFA500 80%); animation: lzTwinkle 1.5s ease-in-out infinite; }
                .lz-icon { font-size: 3.7rem; margin-bottom: 1.1rem; filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.18)); }
                .lz-title {
                    margin: 0.9rem 0 0.3rem; color: #2E7D32; font-weight: 900; font-size: 1.25rem; line-height: 1.3;
                    text-shadow: 0 1px 2px #fff; font-family: ${FONT}; letter-spacing: 1px;
                }
                .lz-card.completed .lz-title { color: #E65100; }
                .lz-meta { font-size: .85rem; font-weight: 800; color: #7a4a00; }
                .lz-stars { display: flex; justify-content: center; gap: .4rem; margin: 1rem 0; }
                .lz-stars span { font-size: 1.7rem; filter: grayscale(1) opacity(.35); }
                .lz-stars span.on { filter: drop-shadow(0 2px 4px rgba(255, 215, 0, 0.6)); animation: lzTwinkle 1.5s ease-in-out infinite; }
                .lz-status {
                    display: inline-flex; align-items: center; gap: .5rem; margin-top: .7rem;
                    padding: .7rem 1.3rem; border-radius: 22px;
                    background: linear-gradient(135deg, #2196F3, #1976D2); color: #fff;
                    font-size: 1.15rem; font-weight: 900; letter-spacing: 1px; font-family: ${FONT};
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18); text-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
                }
                .lz-status.completed {
                    background: linear-gradient(135deg, #fffbea 0%, #fff1c2 100%); color: #8a4b00;
                    border: 2px solid #FFA500; font-size: .95rem; padding: .45rem 1.1rem; text-shadow: none;
                }
                @keyframes lzTwinkle {
                    0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
                    25% { transform: scale(1.2) rotate(5deg); opacity: 0.8; }
                    75% { transform: scale(0.9) rotate(-5deg); opacity: 0.9; }
                }

                /* Modal - same look as GamelevelItems' .custom-modal */
                .lz-modal .modal-dialog { display: flex; align-items: stretch !important; justify-content: center; margin: 16px auto; width: auto; max-width: 100vw; }
                .lz-modal .modal-content { background-image: url('/image/back4.png'); background-size: 100% 100%; background-position: center; color: #222; border-radius: 36px; }
                .lz-modal-title { color: #3e2723; font-weight: 900; text-shadow: 2px 2px 12px #fff, 0 0 12px #e0c68a; font-size: 30px; letter-spacing: 2px; margin: 0; }
                .lz-close { background: #ffe082 !important; border: none !important; color: #3e2723 !important; border-radius: 16px !important; font-weight: 700; font-size: 22px; box-shadow: 0 2px 8px #e0c68a; }
                /* align-self/flex-shrink: the modal body is a height-capped
                   flex row, which otherwise stretches/shrinks the board to
                   75vh and lets tall content (the 15-level puzzle picker)
                   spill out below the white panel instead of scrolling. */
                .lz-board {
                    align-self: flex-start; flex-shrink: 0; height: auto; margin-bottom: 8px;
                    width: 100%; max-width: 1080px; background: rgba(255, 255, 255, 0.94); border-radius: 28px;
                    box-shadow: 0 8px 30px rgba(62, 39, 35, 0.18); animation: lzBoardPop 0.5s cubic-bezier(.68,-0.55,.27,1.55);
                }
                @keyframes lzBoardPop { 0% { transform: scale(0.9); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
                @media (max-width: 900px) {
                    .lz-modal .modal-dialog { margin: 16px auto; }
                    .lz-modal .modal-content { border-radius: 24px; max-height: calc(100vh - 32px); }
                }
                @media (max-width: 700px) {
                    .lz-modal .modal-dialog { margin: 8px auto; }
                    .lz-modal .modal-content { border-radius: 14px; max-height: calc(100vh - 16px); }
                    .lz-modal-title { font-size: 22px; }
                }
                @media (max-width: 575px) {
                    .lz-grid { padding: 2.5rem .5rem; }
                }
            `}</style>
        </>
    );
};

export default LearningZoneGames;
